import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

function CorrectionsNeeded() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/library/corrections-needed`)
      .then(res => setRows(res.data.rows))
      .catch(err => setError(err.response?.data?.error || err.message));
  }, []);

  if (error) return <ErrorBox>{error}</ErrorBox>;
  if (!rows) return <EmptyState>Loading...</EmptyState>;

  const byProduct = new Map();
  for (const r of rows) {
    const key = r.product_code || r.project_code || '(no product code)';
    if (!byProduct.has(key)) byProduct.set(key, []);
    byProduct.get(key).push(r);
  }
  const products = [...byProduct.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div>
      <Header>
        <div>
          <Title>Corrections Needed</Title>
          <Subtitle>
            {rows.length.toLocaleString()} track{rows.length === 1 ? '' : 's'} across {products.length.toLocaleString()} product{products.length === 1 ? '' : 's'} still reference a superseded ISRC
          </Subtitle>
        </div>
        <DownloadBtn href={`${API}/api/library/corrections-needed.csv`}>Download CSV</DownloadBtn>
      </Header>

      {rows.length === 0 && <EmptyState>Nothing outstanding — every release is using its canonical ISRC.</EmptyState>}

      {products.map(([productCode, tracks]) => (
        <ProductCard key={productCode}>
          <ProductHeader>
            <ProductCode>{productCode}</ProductCode>
            <ProductName>{tracks[0].release_name}</ProductName>
          </ProductHeader>
          <Table>
            <thead>
              <tr>
                <th>Track</th>
                <th>Current (wrong) ISRC</th>
                <th></th>
                <th>Correct ISRC</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map((t, i) => (
                <tr key={i}>
                  <td>{t.track_position ?? '—'}</td>
                  <td>
                    <Mono wrong>{t.wrong_isrc}</Mono>
                    <IsrcLabel>
                      {t.wrong_isrc_title || <em>unknown title</em>}
                      {t.wrong_isrc_version ? <VersionTag> [{t.wrong_isrc_version}]</VersionTag> : null}
                    </IsrcLabel>
                  </td>
                  <td><Arrow>→</Arrow></td>
                  <td>
                    <Mono>{t.correct_isrc}</Mono>
                    <IsrcLabel>
                      {t.correct_isrc_title || <em>unknown title</em>}
                      {t.correct_isrc_version ? <VersionTag> [{t.correct_isrc_version}]</VersionTag> : null}
                    </IsrcLabel>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ProductCard>
      ))}
    </div>
  );
}

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.3px;
  color: var(--text);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-top: 2px;
`;

const DownloadBtn = styled.a`
  background: var(--gradient-sun);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 9px 18px;
  border-radius: var(--radius);
  white-space: nowrap;
  text-decoration: none;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
`;

const ProductCard = styled.div`
  background: var(--bg-2);
  border-radius: var(--radius);
  padding: 14px 16px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 14px;
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
`;

const ProductCode = styled.span`
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 13px;
  color: var(--blue-dark);
  background: var(--blue-dim);
  padding: 3px 10px;
  border-radius: 12px;
`;

const ProductName = styled.span`
  font-size: 13px;
  color: var(--text-2);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead th {
    text-align: left;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-3);
    font-weight: 700;
    padding: 6px 8px;
    border-bottom: 1px solid var(--border);
  }

  tbody td {
    padding: 7px 8px;
    font-size: 12px;
    border-bottom: 1px solid var(--border);
  }

  tbody tr:last-child td { border-bottom: none; }
`;

const VersionTag = styled.span`
  color: var(--text-2);
  font-weight: 600;
`;

const Mono = styled.span`
  display: block;
  font-family: var(--font-mono);
  font-size: 12px;
  color: ${p => p.wrong ? 'var(--red)' : 'var(--green)'};
  font-weight: 600;
`;

const IsrcLabel = styled.span`
  display: block;
  font-size: 11px;
  color: var(--text-2);
  margin-top: 2px;
`;

const Arrow = styled.span`
  color: var(--text-3);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-3);
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 10px 14px;
  border-radius: var(--radius);
  margin-bottom: 14px;
  font-size: 13px;
`;

export default CorrectionsNeeded;
